/**
 * Formattazione localizzata. L'applicazione è italiana: date GG/MM/AAAA,
 * valuta in euro, separatore decimale virgola. Tutto passa da qui, così
 * cambiare locale un giorno è una modifica sola.
 */

const LOCALE = 'it-IT'

const timeFormatter = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
})

const dateFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

const dateTimeFormatter = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const currencyFormatter = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const numberFormatter = new Intl.NumberFormat(LOCALE)

/** Orario breve, es. "14:05". */
export function formatTime(iso: string): string {
  return timeFormatter.format(new Date(iso))
}

/** Data, es. "06/08/2026". */
export function formatDate(iso: string): string {
  return dateFormatter.format(new Date(iso))
}

/** Data e ora, es. "06/08/2026, 14:05". */
export function formatDateTime(iso: string): string {
  return dateTimeFormatter.format(new Date(iso))
}

/** Prezzo mensile di un modulo, es. "39 €/mese". */
export function formatMonthlyPrice(amount: number): string {
  return `${currencyFormatter.format(amount)}/mese`
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value)
}

/**
 * Distanza dal presente in forma breve, per gli storici: "adesso", "12 min",
 * "3 h", "5 g". Oltre la settimana passa alla data, che è più utile di
 * "37 giorni fa".
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime()
  const minutes = Math.floor(diffMs / 60_000)

  if (minutes < 1) return 'adesso'
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h`

  const days = Math.floor(hours / 24)
  if (days <= 7) return `${days} g`

  return formatDate(iso)
}

/**
 * Giorni di calendario fra un istante e adesso: 0 è oggi, 1 ieri.
 *
 * Conta i giorni, non le 24 ore. Una ricerca di ieri alle 23:50 sta sotto
 * "ieri" anche se è successa un'ora fa, perché è lì che la cerca chi la cerca.
 * I due estremi vengono azzerati a mezzanotte locale prima del confronto, e la
 * differenza arrotondata: così il giorno del cambio d'ora, che dura 23 o 25
 * ore, non sposta il conteggio di uno.
 */
export function calendarDaysAgo(iso: string, now: Date = new Date()): number {
  const then = new Date(iso)
  const thenMidnight = new Date(then.getFullYear(), then.getMonth(), then.getDate())
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((nowMidnight.getTime() - thenMidnight.getTime()) / 86_400_000)
}

/** Intestazione di un gruppo di storico: "Oggi", "Ieri", poi la data. */
export function dayGroupLabel(iso: string, now: Date = new Date()): string {
  const days = calendarDaysAgo(iso, now)
  if (days === 0) return 'Oggi'
  if (days === 1) return 'Ieri'
  return formatDate(iso)
}

/** Accordo singolare/plurale, perché l'italiano non perdona "1 immagini". */
export function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm
}

/** "3 immagini", "1 immagine". */
export function countLabel(count: number, singular: string, pluralForm: string): string {
  return `${formatNumber(count)} ${plural(count, singular, pluralForm)}`
}
